/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id A key/value object specifying which values to replace
 */
const externalVideoEmbedSrc = (host = 'youtube', id, autoplay=false) => {
  let src = ''
  switch (host) {
    case 'youtube':
      src = `https://www.youtube.com/embed/${id}?${autoplay && `autoplay=1&amp;`}controls=1&amp;enablejsapi=1&amp;loop=1&amp;modestbranding=1&amp;origin=${encodeURI(__GLOBAL__.domain)}&amp;playsinline=1&amp;rel=0`
      break
    case 'vimeo':
      src = `https://player.vimeo.com/video/${id}?${autoplay && `autoplay=1&amp;`}byline=0&amp;controls=1&amp;loop=1&amp;playsinline=1&amp;title=0`
      break
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")')
  }
  return src
}

class AdaptiveVideo extends HTMLElement {
  constructor () {
    super()

    this.type = this.dataset.type
    this.video = JSON.parse(atob(this.dataset.video || 'e30='))
    this.url = this.dataset.url
    this.isSecondaryMedia = this.getPreviousSibling(this, 'adaptive-image')
    this.autoplay = this.dataset.autoplay === 'true'
    this.controls = this.dataset.controls === 'true'
    this.loop = this.dataset.loop === 'true'
    this.id = this.dataset.id || this.video?.external_id || ''
    this.host = this.dataset.host || this.video?.host
    this.alt = this.dataset.alt || this.video?.alt
    this.aspectRatio =
      (this.dataset.aspectRatio === 'wide' && 16/9)
      || (this.dataset.aspectRatio === 'full' && 4/3)
      || parseFloat(this.dataset.aspectRatio)
      || this.video?.aspect_ratio
      || 16/9
    this.posterImage = this.dataset.posterImage
    this.observeIntersection = this.dataset.observeIntersection === 'true' || false

    this.intersecting = false

    this.attachShadow({ mode: 'open' })
    this.render()
    this.handleObserveIntersection()
    if (this.isSecondaryMedia) { this.autoplayOnHover(this.shadowRoot) }
  }

  getPreviousSibling (elem, selector) {
    let sibling = elem.previousElementSibling;

    if (!selector) return null;

    while (sibling) {
      if (sibling.matches(selector)) {
        return sibling;
      }
      sibling = sibling.previousElementSibling;
    }
  };

  // For Product Cards, if the video is a secondary media following an image media type,
  // only autoplay the video on card hover to ensure that the video is not finished playing by
  // the time a user hovers over the card
  autoplayOnHover (shadowRoot) {
    const card = this.closest('.card-wrapper')
    card.addEventListener('mouseover', function() {
      shadowRoot.querySelector('video').play()
    });
    card.addEventListener('mouseout', function() {
      shadowRoot.querySelector('video').pause()
    });
  }

  updateVideo (data) {
    Object.assign(this, data)
    this.render()
  }

  generateStyles () {
    return `
      <style>
        :host {
          display: block;
          padding-top: calc(1 / ${this.aspectRatio} * 100%);
          position: relative;
        }
        #video-element {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
        }
      </style>
    `
  }

  generateVideoMarkup () {
    let sources = ''

    if (this.type === 'url') {
      sources = `<source src="${this.url}"></source>`
    } else if (this.type === 'video') {
      for (const source of this.video.sources) {
        sources += `<source src="${source.url}" type="${source.mime_type}"></source>`
      }
    }

    if (this.observeIntersection && !this.intersecting) {
      sources = ''
    }

    let markup = `
      <video
        id="video-element"
        playsinline="playsinline"
        ${this.autoplay && `autoplay="autoplay"`}
        ${this.controls && `controls="controls"`}
        ${this.loop && `loop="loop"`}
        preload="none"
        muted="muted"
        aria-label="${this.alt}"
        ${this.posterImage && `poster="${this.posterImage}"` || ''}
      >
        ${sources}
        ${this.posterImage && `<img src="${this.posterImage}" alt="${this.alt}">` || ''}
      </video>
    `

    return markup
  }

  generateIframeMarkup () {
    let src = externalVideoEmbedSrc(this.host, this.id, this.autoplay)

    if (this.observeIntersection && !this.intersecting) {
      src = ''
    }
    return `
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${src}"
        title="${this.alt}">
      </iframe>
    `
  }

  handleObserveIntersection () {
    if (!this.observeIntersection) return

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.intersecting = true
          this.render()

          observer.unobserve(this)
        }
      })
    }, {rootMargin: '0px 0px 200px 0px'})

    observer.observe(this)
  }

  show () {
    this.style.display = 'block'
  }

  hide () {
    this.style.display = 'none'
  }

  render () {
    this.shadowRoot.innerHTML = `
      ${this.generateStyles()}
      ${['hosted_url', 'external_video'].includes(this.type) ? this.generateIframeMarkup() : this.generateVideoMarkup()}
    `
  }
}

window.customElements.define('adaptive-video', AdaptiveVideo)
